import { useState, useRef, useCallback } from "react";
import axios from "axios";
import request, { storeRotatedToken } from "./requests";
import { toast } from "./SnackBarUtils";

const DEFAULT_FOCAL_X = 50;
const DEFAULT_FOCAL_Y = 30;

const useProfilePhoto = ({ onUploaded, onFailed } = {}) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const previewRef = useRef(null);
  const lastImageUrl = useRef(null);
  const handlers = useRef({ onUploaded, onFailed });
  handlers.current = { onUploaded, onFailed };

  const setPreview = useCallback((url) => {
    if (previewRef.current && previewRef.current !== url) {
      URL.revokeObjectURL(previewRef.current);
    }
    previewRef.current = url;
    setPreviewUrl(url);
  }, []);

  const syncToProfileImage = useCallback((imageUrl) => {
    if (previewRef.current && imageUrl && imageUrl !== lastImageUrl.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
      setPreviewUrl(null);
    }
    lastImageUrl.current = imageUrl;
  }, []);

  const upload = useCallback(
    async (file) => {
      if (!file) return false;

      setPreview(URL.createObjectURL(file));

      const body = new FormData();
      body.append("photo", file);
      body.append("Content-Type", "image/jpg");
      const token = localStorage.getItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
      );

      setLoading(true);
      try {
        const result = await axios({
          url: `${process.env.REACT_APP_TNFT_SERVER}/profile/photo`,
          method: "post",
          data: body,
          headers: {
            "Content-Type": "multipart/form-data",
            Accesstoken: token,
          },
        });
        storeRotatedToken(result);
        await request("profile/update", "post", {
          photoFocalX: DEFAULT_FOCAL_X,
          photoFocalY: DEFAULT_FOCAL_Y,
        });
        toast.success("Profile image updated");
        setLoading(false);
        if (handlers.current.onUploaded) handlers.current.onUploaded();
        return true;
      } catch (e) {
        storeRotatedToken(e?.response);
        setPreview(null);
        setLoading(false);
        toast.error("Error uploading profile image");
        if (handlers.current.onFailed) handlers.current.onFailed();
        return false;
      }
    },
    [setPreview]
  );

  return {
    loading,
    previewUrl,
    syncToProfileImage,
    upload,
    defaultFocal: { x: DEFAULT_FOCAL_X, y: DEFAULT_FOCAL_Y },
  };
};

export default useProfilePhoto;
